import { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { PanelLeftClose, PanelLeft } from "lucide-react";
import { useSidebar } from '@/context/SidebarContext';
import { useTranslation } from "react-i18next";
import { VessLogo } from "@/components/ui/VessLogo";
import { cn } from "@/lib/utils";

interface BaseSidebarProps {
  brandLabel?: string;
  version?: string;
  children: ReactNode;
  extra?: ReactNode;
  homeHref?: string;
}

export const BaseSidebar = ({
  brandLabel = "founder's hub.",
  version = "v3.1",
  children,
  extra,
  homeHref,
}: BaseSidebarProps) => {
  const { isCollapsed, setIsCollapsed } = useSidebar();
  const { t } = useTranslation();

  return (
    <aside
      className={cn(
        "fixed top-0 left-0 h-screen border-r bg-background flex flex-col transition-all duration-300 z-40 overflow-hidden",
        isCollapsed ? "w-[60px]" : "w-[240px]"
      )}
    >
      <div className={cn(
        "h-14 flex items-center border-b shrink-0",
        isCollapsed ? "justify-center px-2" : "justify-between px-4"
      )}>
        {!isCollapsed && (
          homeHref ? (
            <Link to={homeHref} className="flex items-center gap-2.5 hover:opacity-80 transition-opacity">
              <img src="/images/logo_small.png" alt={brandLabel} className="h-5 w-5" />
              <VessLogo variant="muted" size="md" label={brandLabel} />
            </Link>
          ) : (
            <div className="flex items-center gap-2.5">
              <img src="/images/logo_small.png" alt={brandLabel} className="h-5 w-5" />
              <VessLogo variant="muted" size="md" label={brandLabel} />
            </div>
          )
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors h-8 w-8 p-0"
        >
          {isCollapsed ? (
            <PanelLeft className="h-4 w-4" />
          ) : (
            <PanelLeftClose className="h-4 w-4" />
          )}
          <span className="sr-only">{t('sidebar.toggleSidebar')}</span>
        </Button>
      </div>

      {children}

      {extra && (
        <div className="border-t shrink-0">
          {extra}
        </div>
      )}

      <div className={cn(
        "border-t shrink-0",
        isCollapsed ? "px-2 py-3 flex justify-center" : "px-4 py-3"
      )}>
        {isCollapsed ? (
          homeHref ? (
            <Link to={homeHref} className="flex flex-col items-center gap-0.5 hover:opacity-80 transition-opacity">
              <img src="/images/logo_small.png" alt={brandLabel} className="h-5 w-5 opacity-60" />
              <span className="text-[10px] text-muted-foreground">{version}</span>
            </Link>
          ) : (
            <div className="flex flex-col items-center gap-0.5">
              <img src="/images/logo_small.png" alt={brandLabel} className="h-5 w-5 opacity-60" />
              <span className="text-[10px] text-muted-foreground">{version}</span>
            </div>
          )
        ) : (
          <div className="flex items-center justify-between">
            <VessLogo variant="muted" size="sm" label={brandLabel} />
            <span className="text-[10px] text-muted-foreground">{version}</span>
          </div>
        )}
      </div>
    </aside>
  );
};
